import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { AppState, Platform } from 'react-native'
import * as Linking from 'expo-linking'
import { supabase } from './supabase'
import { fetchUserProfile } from './auth'
import i18n from 'i18next'
import { storage } from './storage'
import { DEFAULT_COMPETITION_ID, COMPETITIONS, isValidCompetition } from './competitions'
import { useBrandColors } from './ThemeContext'
import { registerForPushNotifications, unregisterPushNotifications, refreshPushTokenIfChanged } from './notifications'

// ── OneSignal: solo web per ora, su mobile si integrerà dopo ────────
async function syncOneSignal(userId) {
  if (Platform.OS !== 'web') return
  if (typeof window === 'undefined' || !window.OneSignal) return
  try {
    if (userId) await window.OneSignal.login(userId)
    else await window.OneSignal.logout()
  } catch (e) {
    console.warn('OneSignal sync:', e.message)
  }
}

// ── Tema: su web usa data-theme, su mobile è gestito da state ───────
function applyTheme(theme) {
  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', theme ?? 'dark')
  }
}

// Dopo quanto tempo in background l'app, riaperta, riporta alla lega di default.
// Prima di questo tempo resti nella lega in cui stavi; con l'app chiusa del tutto riparte sempre dalla default.
const RESET_LEAGUE_AFTER_MS = 30 * 60 * 1000

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const { brandColors } = useBrandColors()
  const [user, setUser]               = useState(null)
  const [profile, setProfile]         = useState(null)
  const [loading, setLoading]         = useState(true)
  const [leagueId, setLeagueId]       = useState(1)
  const [leagueName, setLeagueName]   = useState('Lega Generale')
  const [allLeagues, setAllLeagues]   = useState([])    // tutte le leghe dell'utente, di tutte le competizioni
  const [competitionId, setCompetitionId] = useState(DEFAULT_COMPETITION_ID)  // 1 uomini, 2 donne: due "app" separate, ognuna con le sue leghe
  const leagueIdRef     = useRef(1)       // lega attiva, leggibile dentro callback e listener
  const competitionRef  = useRef(DEFAULT_COMPETITION_ID)
  // transizione a schermo intero al cambio uomini/donne (vedi components/CompetitionTransition.jsx); `id` cambia sempre per rilanciare l'animazione
  const [flash, setFlash] = useState(null)   // { colors: [dark, brand, light], label, id } | null
  const allLeaguesRef   = useRef([])
  const appliedUserRef  = useRef(null)    // utente di cui la sessione e' gia' stata caricata

  const setActiveLeague = useCallback((id, name) => {
    leagueIdRef.current = id
    setLeagueId(id)
    setLeagueName(name)
  }, [])

  // Le leghe che l'app vede (switcher, elenchi, classifiche) sono solo quelle della competizione attiva
  const userLeagues = useMemo(() => allLeagues.filter(l => (l.id_competition ?? 1) === competitionId), [allLeagues, competitionId])

  // Preferenza locale della lega per competizione: gli uomini mantengono la chiave storica
  const leagueKey = (userId, comp) => (comp === DEFAULT_COMPETITION_ID ? `leagueId_${userId}` : `leagueId_${userId}_${comp}`)

  // Sceglie la lega attiva DENTRO una competizione. Senza leghe lì (es. donne, mai entrata) leagueId resta null:
  // la schermata mostra "entra nella lega ufficiale" (NoLeagueState), le altre schermate non vengono montate.
  const pickLeague = useCallback(async (data, comp, userId, defaultFromProfile, keepCurrent) => {
    const inComp = data.filter(l => (l.id_competition ?? 1) === comp)
    if (!inComp.length) { setActiveLeague(null, null); return }
    if (keepCurrent) {
      const current = inComp.find(l => l.id_league === leagueIdRef.current)
      if (current) { setLeagueName(current.name); return }
    }
    const savedLocal = userId ? await storage.get(leagueKey(userId, comp)) : null
    const preferredId = (comp === DEFAULT_COMPETITION_ID ? defaultFromProfile : null) || (savedLocal ? parseInt(savedLocal) : null)
    const active = inComp.find(l => l.id_league === preferredId) ?? inComp[0]
    setActiveLeague(active.id_league, active.name)
  }, [setActiveLeague])

  // keepCurrent: aggiorna l'elenco ma non sposta l'utente dalla lega in cui sta navigando
  // (se non e' piu' tra le sue leghe, ad es. l'ha lasciata, ricade sulla default).
  const loadLeagues = useCallback(async (userId, defaultFromProfile, { keepCurrent = false } = {}) => {
    if (!userId) return
    const { data } = await supabase.rpc('fn_user_leagues', { p_id_user: userId })
    if (!data?.length) return

    allLeaguesRef.current = data
    setAllLeagues(data)
    await pickLeague(data, competitionRef.current, userId, defaultFromProfile, keepCurrent)
  }, [pickLeague])

  const signOut = useCallback(async () => {
    try {
      if (user?.id) await unregisterPushNotifications(user.id)
      await supabase.auth.signOut()
    } catch (err) {
      console.error('Errore logout:', err)
    }
  }, [user])

  useEffect(() => {
    let mounted = true
    let linkingSub = null

    const applySession = async (session) => {
      if (!session?.user) {
        if (mounted) {
          setUser(null); setProfile(null)
          appliedUserRef.current = null; allLeaguesRef.current = []; setAllLeagues([]); setActiveLeague(1, 'Lega Generale')
          competitionRef.current = DEFAULT_COMPETITION_ID; setCompetitionId(DEFAULT_COMPETITION_ID)
          applyTheme('dark'); i18n.changeLanguage('it')
          setLoading(false)
          syncOneSignal(null)
        }
        return
      }

      if (mounted) setUser(session.user)

      // Rinnovo token, rientro in primo piano e simili rilanciano l'evento per lo stesso utente:
      // non si ricarica nulla (altrimenti la lega attiva tornava alla default mentre navigavi).
      // Si salta solo se il caricamento per questo utente e' GIA' COMPLETATO: se e' ancora in corso (o e'
      // rimasto appeso) un secondo tentativo, come quello di riserva dopo 4 secondi, deve poter ripartire.
      if (appliedUserRef.current === session.user.id) return

      try {
        const p = await fetchUserProfile(session.user.id)
        if (!mounted) return
        setProfile(p)
        applyTheme(p?.theme ?? 'dark')
        if (p?.language) i18n.changeLanguage(p.language)
        const comp = isValidCompetition(p?.id_competition_default) ? p.id_competition_default : DEFAULT_COMPETITION_ID
        competitionRef.current = comp; setCompetitionId(comp)
        await loadLeagues(session.user.id, p?.id_league_default)
        if (!mounted) return
        appliedUserRef.current = session.user.id

        syncOneSignal(session.user.id)
        await registerForPushNotifications(session.user.id)
        refreshPushTokenIfChanged(session.user.id)

      } catch (err) {
        if (!mounted) return
        console.error('[AuthContext] applySession error:', err)
      } finally {
        if (mounted) setLoading(false)
      }
    }

    // ─── Deep link OAuth Google (solo mobile) ─────────────────────
    if (Platform.OS !== 'web') {
      const handleUrl = async ({ url }) => {
        if (!url) return
        if (url.includes('access_token')) {
          const hashPart = url.split('#')[1]
          if (hashPart) {
            const params = Object.fromEntries(new URLSearchParams(hashPart))
            if (params.access_token && params.refresh_token) {
              await supabase.auth.setSession({
                access_token: params.access_token,
                refresh_token: params.refresh_token,
              })
            }
          }
        }
      }

      linkingSub = Linking.addEventListener('url', handleUrl)

      // Aspetta che onAuthStateChange sia registrato prima di processare
      // l'URL iniziale — evita la race condition al primo click
      setTimeout(() => {
        Linking.getInitialURL().then(url => {
          if (url) handleUrl({ url })
        })
      }, 100)
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return
        if (event === 'SIGNED_OUT') {
          setUser(null); setProfile(null)
          appliedUserRef.current = null; allLeaguesRef.current = []; setAllLeagues([]); setActiveLeague(1, 'Lega Generale')
          competitionRef.current = DEFAULT_COMPETITION_ID; setCompetitionId(DEFAULT_COMPETITION_ID)
          applyTheme('dark'); i18n.changeLanguage('it')
          setLoading(false)
          syncOneSignal(null)
          return
        }
        applySession(session)
      }
    )

    const fallback = setTimeout(async () => {
      if (!mounted || !loading) return
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (mounted) await applySession(session)
      } catch {
        if (mounted) setLoading(false)
      }
    }, 4000)

    return () => {
      mounted = false
      clearTimeout(fallback)
      subscription.unsubscribe()
      linkingSub?.remove()
    }
  }, [loadLeagues, setActiveLeague])

  // Riapertura dal background: torna alla lega di default solo se l'app e' stata in background a lungo
  const profileRef = useRef(null)
  profileRef.current = profile
  useEffect(() => {
    if (!user?.id) return
    let backgroundAt = null
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'background') { backgroundAt = Date.now(); return }
      if (state === 'active' && backgroundAt) {
        const away = Date.now() - backgroundAt
        backgroundAt = null
        if (away >= RESET_LEAGUE_AFTER_MS) loadLeagues(user.id, profileRef.current?.id_league_default)
      }
    })
    return () => sub.remove()
  }, [user?.id, loadLeagues])

  const updateTheme = async (theme) => {
    if (!user) return
    applyTheme(theme)
    setProfile(prev => ({ ...prev, theme }))
    await supabase.from('dim_user').update({ theme }).eq('id_user', user.id)
  }

  const switchLeague = useCallback(async (leagueOrId) => {
    let targetId
    let targetName

    if (typeof leagueOrId === 'object') {
      targetId = leagueOrId.id_league
      targetName = leagueOrId.name
    } else {
      targetId = leagueOrId
    }
    // la lega puo' essere di un'altra competizione (notifica, link, lega appena creata): in quel caso cambia anche competizione
    const match = allLeaguesRef.current.find(l => l.id_league === targetId)
    if (targetName == null) targetName = match ? match.name : 'Lega'
    const targetComp = match?.id_competition ?? leagueOrId?.id_competition
    if (isValidCompetition(targetComp) && targetComp !== competitionRef.current) {
      competitionRef.current = targetComp
      setCompetitionId(targetComp)
      if (user?.id) {
        setProfile(prev => ({ ...prev, id_competition_default: targetComp }))
        supabase.from('dim_user').update({ id_competition_default: targetComp }).eq('id_user', user.id).then(() => {}, () => {})
      }
    }

    setActiveLeague(targetId, targetName)

    if (user?.id) {
      await storage.set(leagueKey(user.id, competitionRef.current), String(targetId))
    }
  }, [user, setActiveLeague])

  // Cambia competizione (uomini/donne): ricalcola la lega attiva dentro la nuova competizione e salva la preferenza su dim_user
  const switchCompetition = useCallback(async (id) => {
    if (!isValidCompetition(id) || id === competitionRef.current) return
    setFlash({ colors: [brandColors.rosaDark, brandColors.rosa, brandColors.rosaLight], label: i18n.t(COMPETITIONS.find(c => c.id === id)?.labelKey ?? ''), id: Date.now() })
    competitionRef.current = id
    setCompetitionId(id)
    await pickLeague(allLeaguesRef.current, id, user?.id, profileRef.current?.id_league_default, false)
    if (user?.id) {
      setProfile(prev => ({ ...prev, id_competition_default: id }))
      try { await supabase.from('dim_user').update({ id_competition_default: id }).eq('id_user', user.id) }
      catch (err) { console.warn('[Auth] switchCompetition:', err?.message) }
    }
  }, [user, pickLeague, brandColors])

  // Avatar persona: dopo il salvataggio dall'editor aggiorna il profilo in memoria (Home e invito lo leggono da qui)
  const setPersonAvatar = useCallback((person) => {
    setProfile(prev => {
      const next = { ...(prev?.avatar_settings ?? {}) }
      if (person) next.person = person
      else delete next.person
      return { ...prev, avatar_settings: next }
    })
  }, [])

  // Lega di default: salva su DB e aggiorna il profilo in memoria (serve al ritorno dal background)
  const setDefaultLeague = useCallback(async (leagueIdNew) => {
    if (!user?.id) return
    await supabase.from('dim_user').update({ id_league_default: leagueIdNew }).eq('id_user', user.id)
    setProfile(prev => ({ ...prev, id_league_default: leagueIdNew }))
  }, [user])

  const refreshLeagues = useCallback(async () => {
    if (user?.id) await loadLeagues(user.id, profile?.id_league_default, { keepCurrent: true })
  }, [user, loadLeagues, profile])

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      loading,
      isAdmin:       profile?.is_admin ?? false,
      nickname:      profile?.nickname ?? '',
      language:      profile?.language ?? 'it',
      isLoggedIn:    !!user,
      theme:         profile?.theme ?? 'dark',
      avatarSettings: profile?.avatar_settings ?? null,
      setPersonAvatar,
      updateTheme,
      leagueId,
      leagueName,
      userLeagues,
      allLeagues,
      competitionId,
      switchCompetition,
      flash,
      noLeague:      leagueId == null,        // competizione attiva senza nessuna lega dell'utente (vedi NoLeagueState)
      switchLeague,
      setDefaultLeague,
      refreshLeagues,
      signOut,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth deve essere usato dentro <AuthProvider>')
  return ctx
}